/**
 * Fibonacci / Gann level sets and their math (docs/TRADINGVIEW-REFERENCE.md §13.7). Prices are in
 * ticks; a level's price is exact float ticks (drawn where it falls), rounded to a whole tick only
 * for the printed label. Times are fractional bar indices (the engine maps them to x).
 */
import type { FibLevel } from "./types.ts";

const L = (value: number, color: string, visible = true): FibLevel => ({ value, color, visible });

/** TradingView's retracement defaults [I]: 11 levels shown, 13 more present but hidden (24). */
export const DEFAULT_FIB_LEVELS: readonly FibLevel[] = [
  L(0, "#787B86"),
  L(0.236, "#F23645"),
  L(0.382, "#FF9800"),
  L(0.5, "#4CAF50"),
  L(0.618, "#089981"),
  L(0.786, "#00BCD4"),
  L(1, "#787B86"),
  L(1.618, "#2962FF"),
  L(2.618, "#F23645"),
  L(3.618, "#9C27B0"),
  L(4.236, "#E91E63"),
  L(1.272, "#FF9800", false),
  L(1.414, "#F23645", false),
  L(2, "#089981", false),
  L(2.272, "#FF9800", false),
  L(2.414, "#4CAF50", false),
  L(3, "#00BCD4", false),
  L(3.272, "#787B86", false),
  L(3.414, "#2962FF", false),
  L(4, "#F23645", false),
  L(4.272, "#9C27B0", false),
  L(4.414, "#E91E63", false),
  L(4.618, "#FF9800", false),
  L(4.764, "#089981", false),
];

/** SPEC §12.5 "OTE preset": 0, 0.5, 0.618, 0.705, 0.79, 1, −0.27, −0.62 (0.62–0.79 highlighted). */
export const OTE_FIB_LEVELS: readonly FibLevel[] = [
  L(0, "#787B86"),
  L(0.5, "#4CAF50"),
  L(0.618, "#089981"),
  L(0.705, "#FF9800"),
  L(0.79, "#089981"),
  L(1, "#787B86"),
  L(-0.27, "#2962FF"),
  L(-0.62, "#2962FF"),
];

export const FIB_TIME_ZONE_LEVELS: readonly FibLevel[] = [0, 1, 2, 3, 5, 8, 13, 21, 34, 55, 89].map((v) =>
  L(v, "#2962FF"),
);

export const TREND_FIB_TIME_LEVELS: readonly FibLevel[] = [
  L(0, "#787B86"),
  L(0.382, "#F23645"),
  L(0.5, "#4CAF50"),
  L(0.618, "#089981"),
  L(1, "#787B86"),
  L(1.382, "#00BCD4"),
  L(1.618, "#2962FF"),
  L(2, "#9C27B0"),
  L(2.382, "#E91E63", false),
  L(2.618, "#F23645"),
  L(3, "#FF9800", false),
  L(4.236, "#E91E63"),
];

export const FIB_FAN_LEVELS: readonly FibLevel[] = [
  L(0, "#787B86"),
  L(0.25, "#F23645"),
  L(0.382, "#FF9800"),
  L(0.5, "#4CAF50"),
  L(0.618, "#089981"),
  L(0.75, "#00BCD4"),
  L(1, "#787B86"),
];

export const FIB_CIRCLE_LEVELS: readonly FibLevel[] = [
  L(0.236, "#F23645"),
  L(0.382, "#FF9800"),
  L(0.5, "#4CAF50"),
  L(0.618, "#089981"),
  L(0.786, "#00BCD4"),
  L(1, "#787B86"),
  L(1.618, "#2962FF"),
  L(2.618, "#F23645"),
  L(3.618, "#9C27B0"),
  L(4.236, "#E91E63"),
];

export const GANN_BOX_LEVELS: readonly FibLevel[] = [
  L(0, "#787B86"),
  L(0.25, "#FF9800"),
  L(0.382, "#089981"),
  L(0.5, "#4CAF50"),
  L(0.618, "#00BCD4"),
  L(0.75, "#2962FF"),
  L(1, "#787B86"),
];

/** Gann fan price/time ratios (8×1 … 1×8); 1 = the 1×1 line through the second point. */
export const GANN_FAN_LEVELS: readonly FibLevel[] = [
  L(8, "#FF9800"),
  L(4, "#089981"),
  L(3, "#4CAF50"),
  L(2, "#00BCD4"),
  L(1, "#2962FF"),
  L(1 / 2, "#9C27B0"),
  L(1 / 3, "#E91E63"),
  L(1 / 4, "#F23645"),
  L(1 / 8, "#787B86"),
];

/**
 * Fib retracement level price (ticks). TradingView convention: level 0 sits at the **second**
 * point, 1 at the first; `reverse` swaps the ends.
 */
export function retracementPrice(p1: number, p2: number, level: number, reverse = false): number {
  return reverse ? p1 + (p2 - p1) * level : p2 + (p1 - p2) * level;
}

/** Trend-based extension: the 1→2 move projected from the third point (`reverse` = against it). */
export function extensionPrice(p1: number, p2: number, p3: number, level: number, reverse = false): number {
  return p3 + (reverse ? -1 : 1) * (p2 - p1) * level;
}

/** Fib time zone: bar index of a level (levels are bar multiples of the 1→2 distance). */
export function timeZoneIndex(i1: number, i2: number, level: number): number {
  return i1 + (i2 - i1) * level;
}

/** Trend-based fib time: level × the 1→2 bar distance, counted from the third point. */
export function trendTimeIndex(i1: number, i2: number, i3: number, level: number): number {
  return i3 + (i2 - i1) * level;
}

/** Level label: "0.618", "61.8%" or "" (fibLabels), optionally with the price in parentheses. */
export function fibLabel(level: number, mode: "values" | "percent" | "none", price: string | null): string {
  const v = mode === "none" ? "" : mode === "percent" ? `${trim(level * 100, 1)}%` : trim(level, 3);
  if (price === null) return v;
  return v ? `${v} (${price})` : price;
}

function trim(v: number, decimals: number): string {
  const s = v.toFixed(decimals);
  return s.includes(".") ? s.replace(/0+$/, "").replace(/\.$/, "") : s;
}

/** Sanitise a stored level list (max 24, finite values, `#hex` colours). */
export function sanitizeLevels(raw: unknown, fallback: readonly FibLevel[]): FibLevel[] {
  if (!Array.isArray(raw)) return fallback.map((l) => ({ ...l }));
  const out: FibLevel[] = [];
  for (const x of raw.slice(0, 24)) {
    if (typeof x !== "object" || x === null) continue;
    const r = x as Record<string, unknown>;
    const value = r["value"];
    if (typeof value !== "number" || !Number.isFinite(value) || Math.abs(value) > 1000) continue;
    const color = typeof r["color"] === "string" && HEX.test(r["color"]) ? r["color"] : "#787B86";
    out.push({ value, color, visible: r["visible"] !== false });
  }
  return out;
}

export const HEX = /^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/;
